import {
	Component,
	EventEmitter,
	Input,
	Output,
	signal
}                                from '@angular/core';
import { BaseComponent }         from '@app/core/bases/base-component/base.component';
import {
	FilterDatepickerType,
	FilterDateRangePickerType,
	FilterListType,
	FilterSelectArchivingErsType,
	FilterSelectArchivingKeywordType,
	FilterSelectArchivingTssType,
	FilterSwitchType,
	FilterTextType,
	FilterType,
	FilterTypes,
	FilterValueType,
	IActiveFilterData,
	IActiveFilters,
	IFilter,
	IFiltersWithAsyncInit
}                                from '@shared/list-operations/model/filter.model';
import {
	IListOperationsConfig,
	IResetEvent,
	ListOperationsEnum,
	ListOperationsIdentifierEnum,
	ListOperationsResetEnum,
	ListOperationsResetModeEnum,
	ListOperationsResetType
}                                from '@shared/list-operations/model/list-operations.model';
import { ListOperationsService } from '@shared/list-operations/services/list-operations.service';
import { ObjectUtils }           from '@utils/helpers';
import { Subscription }          from 'rxjs';
import {
	take,
	takeUntil
}                                from 'rxjs/operators';

@Component( {
	selector: 'app-filter[defaultFilterData]',
	templateUrl: './filter.component.html',
	styleUrls: [ './filter.component.scss' ]
} )

export class FilterComponent extends BaseComponent {

	private _activeFilters: IActiveFilters;

	private _allAsyncFiltersAreInitialized: boolean;

	private _defaultFilterData: FilterType[];

	private _filtersWithAsyncInitMap: Map<number, boolean> = new Map<number, boolean>();

	private _listOperationIdentifier: ListOperationsIdentifierEnum;

	private _resetListOperationsSubscription: Subscription;

	activeOperation: boolean;

	showBody = signal(true);

	filterTypes = FilterTypes;

	hasAppliedFilter = signal(false);

	filterData: IFilter[];

	resetEventEmitter: EventEmitter<IResetEvent> = new EventEmitter<IResetEvent>();

	listOperationsResetMode = ListOperationsResetModeEnum;

	get activeFilters() {
		return this._activeFilters;
	}

	@Input( 'activeListOperationChange' )
	set activeListOperationChange( activeListOperation: ListOperationsEnum ) {
		setTimeout( () => {
			if ( activeListOperation !== ListOperationsEnum.FILTER ) {
				this.showBody.set(false);
			}
		} );
	}

	@Input( 'defaultFilterData' )
	set defaultFilterData( defaultFilterData: FilterType[] ) {
		if ( defaultFilterData?.length ) {
			this._defaultFilterData = [ ...defaultFilterData ];
			this.setDefault();
			this.checkAndSetAsyncFilterInitializationState();
			this.emitFilterInitialized();
		}
	}

	get listOperationIdentifier() {
		return this._listOperationIdentifier;
	}

	@Input( 'listOperationIdentifier' )
	set listOperationIdentifier( listOperationIdentifier: ListOperationsIdentifierEnum ) {

		this._listOperationIdentifier = listOperationIdentifier;
		this._resetListOperationsSubscription?.unsubscribe();

		this._resetListOperationsSubscription =
			this.listOperationsService.select.resetListOperationsData(
				listOperationIdentifier,
				'FilterComponent:set listOperationIdentifier'
			)
			.pipe( takeUntil( this.ngUnsubscribe )
			)
			.subscribe( ( resetData: ListOperationsResetType ) => {
				if (
					resetData?.listOperator === ListOperationsResetEnum.ALL
				) {
					switch ( resetData?.resetMode ) {
						case ListOperationsResetModeEnum.CLEAR:
							if ( this.filterData?.length ) {
								this.clearAllFilter();
								this.emitFilterChanges();
							}
							break;

						case ListOperationsResetModeEnum.DEFAULT:
						case ListOperationsResetModeEnum.RESTORE:
							this.resetAllFilter( resetData?.resetMode );
							break;
					}
				}
			} );
	}

	// TODO temporary flag prop to distinguish between old and new, will be removed when old has been completely replaced by new
	@Input() sidebar: boolean;

	@Output() operationInitializedEmitter: EventEmitter<IActiveFilters> = new EventEmitter();

	@Output() operationChangedEmitter: EventEmitter<IActiveFilters> = new EventEmitter();

	@Output() activeListOperationEmitter: EventEmitter<ListOperationsEnum> = new EventEmitter();

	constructor(
		private readonly listOperationsService: ListOperationsService
	) {
		super();
	}

	private emitFilterInitialized(): void {
		if ( this._allAsyncFiltersAreInitialized ) {
			this.applyFilter();
			this.operationInitializedEmitter.emit( this._activeFilters );
		}
	}

	private checkAndSetAsyncFilterInitializationState(): void {
		let allAsyncFiltersAreInitialized = true;

		for ( const filterIsInitialized of this._filtersWithAsyncInitMap.values() ) {
			if ( filterIsInitialized ) { continue; }
			allAsyncFiltersAreInitialized = false;
			break;
		}

		this._allAsyncFiltersAreInitialized = allAsyncFiltersAreInitialized;
	}

	private resetConflictingFilters( sourceFilter?: IFilter ): void {
		this.logger.debug( this.filterData );
		this.filterData.forEach( ( targetFilter?: IFilter ) => {
			if (
				sourceFilter?.filterId === targetFilter?.filterId &&
				sourceFilter?.id !== targetFilter?.id
			) {
				this.clearFilter( targetFilter, true, true );
			}
		} );
		this.logger.debug( this.filterData );
	}

	private setDefault(): void {
		this._allAsyncFiltersAreInitialized = false;
		this._filtersWithAsyncInitMap.clear();

		this.filterData = JSON.parse( JSON.stringify( this._defaultFilterData ) ); // a deep copy is needed here

		let applyFilter = false;

		// sort filters
		this.filterData = [ ...this.filterData.sort( ( filterA, filterB ) => filterA.order - filterB.order ) ];

		// set filter default values
		this.filterData.forEach( ( filter: IFilter, filterIndex: number ) => {
			filter.values = filter.values ?? [];
			filter.isRendered = !ObjectUtils.isNullish( filter.isRendered ) ? filter.isRendered : false;
			filter.isDisabled = !ObjectUtils.isNullish( filter.isDisabled ) ? filter.isDisabled : false;

			switch ( filter.type ) {
				case FilterTypes.FILTER_SELECT_ARCHIVING_ERS_TYPE:
				case FilterTypes.FILTER_SELECT_ARCHIVING_KEYWORD_TYPE:
				case FilterTypes.FILTER_SELECT_ARCHIVING_TSS_TYPE:
				case FilterTypes.FILTER_SELECT_BUSINESS_PREMISES_TYPE:
				case FilterTypes.FILTER_SELECT_CONTRACT_TYPE:
				case FilterTypes.FILTER_SELECT_ERS_TYPE:
				case FilterTypes.FILTER_SELECT_ORGANIZATION_TYPE:
				case FilterTypes.FILTER_SELECT_STATIC_ITEMS_TYPE:
				case FilterTypes.FILTER_SELECT_TENANT_TYPE:
				case FilterTypes.FILTER_SELECT_TSS_TYPE:
					filter.selectFilter.search = filter?.selectFilter?.search ?? null;
					this._filtersWithAsyncInitMap.set( filterIndex, false );
					break;

				default:
					this._filtersWithAsyncInitMap.set( filterIndex, true );
			}

			// on existing preset values set and lock applyFilter to true
			if ( !applyFilter && filter.values.length > 0 ) {
				applyFilter = true;
			}
		} );

		// if preset values exist, filter is applied initially
		if ( applyFilter ) {
			this.applyFilter();
		}
	}

	private applyFilter(): void {
		this.hasAppliedFilter.set( false );
		this._activeFilters = {};

		this.filterData.forEach( filter => {

			// is filter in use check
			if ( !!filter.values.length === true ) {
				this.hasAppliedFilter.set( true );

				// create initial filter key/value if not exist
				if ( !this._activeFilters[ filter.filterId ] ) {
					this._activeFilters[ filter.filterId ] = {
						param: []
					};
				}

				switch ( filter.type ) {
					case FilterTypes.FILTER_TEXT_TYPE:
						const textFilterValue: FilterTextType = filter.values[ 0 ] as FilterTextType;

						textFilterValue
						? this._activeFilters[ filter.filterId ] = { param: textFilterValue }
						: delete this._activeFilters[ filter.filterId ];
						break;

					case FilterTypes.FILTER_SWITCH_TYPE:
						this._activeFilters[ filter.filterId ] = {
							param: filter.values[ 0 ] as FilterSwitchType
						};
						break;

					case FilterTypes.FILTER_OPTION_TYPE:
						this._activeFilters[ filter.filterId ] = {
							param: filter.values as FilterListType
						};
						break;

					case FilterTypes.FILTER_SELECT_ARCHIVING_ERS_TYPE:
						this._activeFilters[ filter.filterId ] = {
							param: filter.values as unknown as FilterSelectArchivingErsType,
							restoreValue: filter.selectFilter?.searchRestoreValue
						};
						break;

					case FilterTypes.FILTER_SELECT_ARCHIVING_KEYWORD_TYPE:
						this._activeFilters[ filter.filterId ] = {
							param: filter.values as unknown as FilterSelectArchivingKeywordType,
							restoreValue: filter.selectFilter?.searchRestoreValue
						};
						break;

					case FilterTypes.FILTER_SELECT_ARCHIVING_TSS_TYPE:
						this._activeFilters[ filter.filterId ] = {
							param: filter.values as unknown as FilterSelectArchivingTssType,
							restoreValue: filter.selectFilter?.searchRestoreValue
						};
						break;

					case FilterTypes.FILTER_SELECT_BUSINESS_PREMISES_TYPE:
					case FilterTypes.FILTER_SELECT_CONTRACT_TYPE:
					case FilterTypes.FILTER_SELECT_ERS_TYPE:
					case FilterTypes.FILTER_SELECT_ORGANIZATION_TYPE:
					case FilterTypes.FILTER_SELECT_STATIC_ITEMS_TYPE:
					case FilterTypes.FILTER_SELECT_TENANT_TYPE:
					case FilterTypes.FILTER_SELECT_TSS_TYPE:
						this._activeFilters[ filter.filterId ] = {
							param: filter.values as FilterListType,
							restoreValue: filter.selectFilter?.searchRestoreValue
						};
						break;

					case FilterTypes.FILTER_LIST_TYPE:
						this._activeFilters[ filter.filterId ] = {
							param: filter.values as FilterListType
						};
						break;

					case FilterTypes.FILTER_DATE_PICKER_TYPE:
						this._activeFilters[ filter.filterId ] = {
							param: filter.values[ 0 ] as FilterDatepickerType
						};
						break;

					case FilterTypes.FILTER_DATE_RANGE_PICKER_TYPE:
						this._activeFilters[ filter.filterId ] = {
							param: filter.values[ 0 ] as FilterDateRangePickerType
						};
						break;
				}
			}
		} );

		this.logger.debug( 'FilterComponent:_thisActiveFilters', this._activeFilters );
	}

	private setResetStateForIndividualFilter(
		filter: IFilter,
		resetMode: ListOperationsResetModeEnum,
		blockEmit?: boolean
	) {

		this.listOperationsService.state.resetListOperationsDataSet(
			this._listOperationIdentifier,
			{
				listOperator: ListOperationsResetEnum.FILTER,
				identifier: filter.id,
				resetMode,
				blockEmit
			},
			'FilterComponent:setResetStateForIndividualFilter'
		);
	}

	toggleBodyVisibility(): void {
		this.showBody.set(!this.showBody());
		if ( this.showBody() ) {
			this.activeListOperationEmitter.emit( ListOperationsEnum.FILTER );
		}
	}

	onFilterItemChange( filter: IFilter, activeFilterData?: IActiveFilterData ): void {

		filter.values = Object.assign( [], filter.values );
		switch ( filter.type ) {
			case FilterTypes.FILTER_DATE_PICKER_TYPE:
			case FilterTypes.FILTER_DATE_RANGE_PICKER_TYPE:
			case FilterTypes.FILTER_OPTION_TYPE:
			case FilterTypes.FILTER_SWITCH_TYPE:
			case FilterTypes.FILTER_TEXT_TYPE:
				filter.values[ 0 ] = activeFilterData?.param;
				break;

			case FilterTypes.FILTER_LIST_TYPE:
				filter.values = activeFilterData?.param as FilterValueType[];
				break;

			case FilterTypes.FILTER_SELECT_ARCHIVING_ERS_TYPE:
			case FilterTypes.FILTER_SELECT_ARCHIVING_KEYWORD_TYPE:
			case FilterTypes.FILTER_SELECT_ARCHIVING_TSS_TYPE:
			case FilterTypes.FILTER_SELECT_BUSINESS_PREMISES_TYPE:
			case FilterTypes.FILTER_SELECT_CONTRACT_TYPE:
			case FilterTypes.FILTER_SELECT_ERS_TYPE:
			case FilterTypes.FILTER_SELECT_ORGANIZATION_TYPE:
			case FilterTypes.FILTER_SELECT_STATIC_ITEMS_TYPE:
			case FilterTypes.FILTER_SELECT_TENANT_TYPE:
			case FilterTypes.FILTER_SELECT_TSS_TYPE:
				filter.values = activeFilterData?.param ? [ activeFilterData?.param ] : [];
				filter.selectFilter.searchRestoreValue = activeFilterData?.restoreValue;
				break;
		}

		if ( activeFilterData?.param ) {
			this.resetConflictingFilters( filter );
		}

		if ( this.sidebar ) { this.emitFilterChanges(); }
	}

	resetListOperator( toDefaultValues?: boolean ): void {
		this.activeOperation = false;
		this.showBody.set(false);
		this.hasAppliedFilter.set(false);
		this.clearAllFilter();

		if ( toDefaultValues ) {
			this.setDefault();
		}
	}

	resetAllFilter( resetMode: ListOperationsResetModeEnum ): void {

		// gets configData from store
		this.listOperationsService.select.configData(
			this.listOperationIdentifier,
			'FilterComponent:resetAllFilter'
		)
		.pipe(
			take( 1 ),
			takeUntil( this.ngUnsubscribe )
		)
		.subscribe( ( configData: IListOperationsConfig ) => {

			let filterData: IFilter[];

			// sets filterData by resetMode
			switch ( resetMode ) {
				case ListOperationsResetModeEnum.DEFAULT:
					filterData = (
						configData?.defaultData?.filterData
						? ObjectUtils.deepCopyByRecursion( configData?.defaultData?.filterData )
						: ObjectUtils.deepCopyByRecursion( configData?.filterData )
					) as IFilter[];
					break;

				case ListOperationsResetModeEnum.RESTORE:
					filterData = (
						configData?.restoreData?.filterData
						? ObjectUtils.deepCopyByRecursion( configData?.restoreData?.filterData )
						: ObjectUtils.deepCopyByRecursion( configData?.filterData )
					) as IFilter[];
					break;
			}

			this.filterData?.forEach( ( filter: IFilter ) => {
				/*
				 * assignment of individual properties in order to avoid changing the
				 * reference of 'this.filterData' (otherwise re-initialization of
				 * specialized filters would occur due to ngFor directive in view
				 * template)
				 */
				filter = {
					...filterData.find( ( localFilter: IFilter ) => localFilter.id === filter.id )
				};
				this.resetFilter( filter, resetMode, false );
			} );
		} );
	}

	clearAllFilter(): void {
		this.filterData?.forEach( ( filter: IFilter ) => {
			this.clearFilter( filter, false );
		} );

		// ngrx store approach, used by a few filters only a.t.m.
		this.listOperationsService.state.resetListOperationsDataSet(
			this._listOperationIdentifier,
			{
				listOperator: ListOperationsResetEnum.FILTERS_ALL,
				resetMode: ListOperationsResetModeEnum.CLEAR
			},
			'FilterComponent:clearAllFilter'
		);
	}

	clearFilter( filter: IFilter, updateStore: boolean = true, blockEmit: boolean = false ): void {
		this.resetEventEmitter.emit(
			{ service: filter.service, resetMode: ListOperationsResetModeEnum.CLEAR, blockEmit }
		);

		filter.values = [];

		// ngrx store approach, used by a few filters only a.t.m.
		if ( updateStore ) {
			this.setResetStateForIndividualFilter(
				filter,
				ListOperationsResetModeEnum.CLEAR,
				blockEmit
			);
		}
	}

	resetFilter( filter: IFilter, resetMode: ListOperationsResetModeEnum, updateStore: boolean = true ): void {
		this.resetEventEmitter.emit(
			{ service: filter.service, resetMode }
		);

		// ngrx store approach, used by a few filters only a.t.m.
		if ( updateStore ) {
			this.setResetStateForIndividualFilter( filter, resetMode );
		}
	}

	emitFilterChanges(): void {
		this.applyFilter();
		this.operationChangedEmitter.emit( this._activeFilters );
		this.showBody.set(false);
	}

	onAsyncFilterInit( filtersWithAsyncInit: IFiltersWithAsyncInit ): void {
		this._filtersWithAsyncInitMap.set( filtersWithAsyncInit?.filterId, filtersWithAsyncInit?.isInitialized );
		this.checkAndSetAsyncFilterInitializationState();
		this.logger.debug( 'allAsyncFiltersAreInitialized', this._allAsyncFiltersAreInitialized );
		this.emitFilterInitialized();
	}
}
